import { themeAtom } from "../../../shared/atoms";
import type { AdminCrewReportImportModalProps } from "../../models/admin-crew-report.models";
import AdminCrewReportImportBatchResult from "./AdminCrewReportImportBatchResult";
import AdminCrewReportImportModalActions from "./AdminCrewReportImportModalActions";
import AdminCrewReportImportSummary from "./AdminCrewReportImportSummary";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportImportModal = ({ result, onClose, onExport }: AdminCrewReportImportModalProps) => {
  const theme = useAtomValue(themeAtom);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const hasFailures = result.outcome !== "COMPLETED";

  return (
    <Dialog
      as="div"
      open
      initialFocus={titleRef}
      onClose={onClose}
      data-test="admin-crew-report-import-modal"
      className="admin-crew-report-import-print-root fixed inset-0 z-50"
    >
      <div className="admin-crew-report-import-print-hidden fixed inset-0 bg-black/45" aria-hidden="true" />
      <div className="admin-crew-report-import-print-container fixed inset-0 flex items-center justify-center px-3 py-4">
        <Dialog.Panel
          as="section"
          aria-label="Crew Report import printable summary"
          className={twMerge(
            "admin-crew-report-import-print-report flex max-h-[92vh] w-full max-w-5xl flex-col bg-white shadow-2xl dark:bg-gray-800",
            theme.home.tailwind.textDefault,
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-[#D8E6E3] px-5 py-4 dark:border-gray-700">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase text-green">Crew Report Import</p>
              <Dialog.Title ref={titleRef} tabIndex={-1} className="text-2xl font-semibold outline-none">
                Import run summary
              </Dialog.Title>
              <p className={twMerge("mt-1 text-sm font-medium", hasFailures ? "text-[#BF0002]" : "text-green")}>
                {result.outcome.replace(/_/g, " ")}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close Crew Report import summary"
              className="admin-crew-report-import-print-hidden p-2 text-[#596563] hover:bg-[#E9F4F3] dark:text-gray-300 dark:hover:bg-gray-700"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </header>

          <div className="admin-crew-report-import-print-content min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="grid gap-5">
              <AdminCrewReportImportSummary result={result} />

              <div className="grid gap-4">
                {result.reports.map((report, index) => (
                  <AdminCrewReportImportBatchResult
                    key={report.cycleId}
                    batchNumber={index + 1}
                    batchCount={result.batchCount}
                    report={report}
                  />
                ))}
              </div>
            </div>
          </div>

          <AdminCrewReportImportModalActions onClose={onClose} onExport={onExport} />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AdminCrewReportImportModal;
